import { ReactNode, useRef } from "react";

import { Aperture, Trash, UploadSimple, User } from "@phosphor-icons/react";
import {
  Avatar,
  AvatarImage,
  buttonVariants,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { motion } from "framer-motion";
import { useMemo } from "react";
import { z } from "zod";

import { useUploadImage } from "@/client/services/storage";
import { useCoverLetterStore } from "@/client/stores/cover-letter";
import { CLCollapsibleSection } from "../../CollapsibleSection";

import { CLCustomFieldsSection } from "./custom-fields";
import { CLPictureOptions } from "./picture-options";

export const SenderSection = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { uploadImage } = useUploadImage();

  const setValue = useCoverLetterStore((state) => state.setValue);
  const sender = useCoverLetterStore((state) => state.coverLetter.data.sender);

  const isValidPictureUrl = useMemo(
    () => z.string().url().safeParse(sender.picture.url).success,
    [sender.picture.url],
  );

  const handleSelectImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const response = await uploadImage(file);
      setValue("sender.picture.url", response.data);
    }
  };

  const handleAvatarClick = () => {
    if (isValidPictureUrl) {
      setValue("sender.picture.url", "");
    } else {
      inputRef.current?.click();
    }
  };

  const headerHTML: ReactNode = (
    <div className="flex items-center gap-x-4">
      <User size={18} />
      <h2 className="line-clamp-1 text-base font-bold text-gray-800 lg:text-xl">Sender</h2>
    </div>
  );

  const contentHTML: ReactNode = (
    <main className="grid gap-4 sm:grid-cols-2">
      {/* Picture */}
      <div className="flex items-center gap-x-4 sm:col-span-2">
        <div className="group relative cursor-pointer" onClick={handleAvatarClick}>
          <Avatar className="size-14 bg-secondary">
            <AvatarImage src={sender.picture.url} />
          </Avatar>
          {isValidPictureUrl ? (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-background/30 opacity-0 transition-opacity group-hover:opacity-100">
              <Trash size={16} weight="bold" />
            </div>
          ) : (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-background/30 opacity-0 transition-opacity group-hover:opacity-100">
              <UploadSimple size={16} weight="bold" />
            </div>
          )}
        </div>
        <div className="flex w-full flex-col gap-y-1.5">
          <Label>Picture</Label>
          <div className="flex items-center gap-x-2">
            <input ref={inputRef} hidden type="file" onChange={handleSelectImage} />
            <Input
              placeholder="https://..."
              value={sender.picture.url}
              className="flex-1"
              onChange={(event) => setValue("sender.picture.url", event.target.value)}
            />

            {isValidPictureUrl && (
              <Popover>
                <PopoverTrigger asChild>
                  <motion.button
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className={cn(buttonVariants({ size: "icon", variant: "ghost" }))}
                  >
                    <Aperture />
                  </motion.button>
                </PopoverTrigger>
                <PopoverContent className="w-[360px]">
                  <CLPictureOptions />
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-1.5 sm:col-span-2">
        <Label>Full Name</Label>
        <Input
          value={sender.name}
          onChange={(event) => setValue("sender.name", event.target.value)}
        />
      </div>

      <div className="space-y-1.5">
        <Label>Email</Label>
        <Input
          placeholder="john.doe@example.com"
          value={sender.email}
          onChange={(event) => setValue("sender.email", event.target.value)}
        />
      </div>

      <div className="space-y-1.5">
        <Label>Phone</Label>
        <Input
          placeholder="+1 (123) 4567 7890"
          value={sender.phone}
          onChange={(event) => setValue("sender.phone", event.target.value)}
        />
      </div>

      <div className="space-y-1.5">
        <Label>Location</Label>
        <Input
          value={sender.location}
          onChange={(event) => setValue("sender.location", event.target.value)}
        />
      </div>

      <div className="space-y-1.5">
        <Label>Website</Label>
        <Input
          placeholder="https://example.com"
          value={sender.url.href}
          onChange={(event) =>
            setValue("sender.url", { ...sender.url, href: event.target.value })
          }
        />
      </div>

      <div className="space-y-1.5 sm:col-span-2">
        <Label>LinkedIn</Label>
        <Input
          placeholder="https://linkedin.com/in/..."
          value={sender.linkedinUrl.href}
          onChange={(event) =>
            setValue("sender.linkedinUrl", {
              ...sender.linkedinUrl,
              href: event.target.value,
            })
          }
        />
      </div>

      <CLCustomFieldsSection className="sm:col-span-2" />
    </main>
  );

  return (
    <CLCollapsibleSection id="cl-sender" defaultOpen={true} header={headerHTML} content={contentHTML} />
  );
};
